import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { APP_ENVIRONMENT } from '@core/config/app-environment';
import { EventTrackerResult, EventTrackerService } from '@core/event-tracker';
import { SnackMessageService } from '@shared/components/snack-message/snack-message.service';
import { catchError, finalize, switchMap, throwError } from 'rxjs';
import Swal from 'sweetalert2';
import { isPublicTokenPath } from './auth.interceptor';
import { AuthService } from './auth.service';
import { SessionExpiredError } from './session-expired.error';

/** Set while the session is being closed by a 403, so parallel 403s do not log out (and notify) again. */
let forbiddenLogoutInProgress = false;

/**
 * 401 and 403 handling. 401 is a port of legacy errorInterceptor:
 * - public pages (`/withdraws`, `/queue-manager`, `/auth`): the error is propagated as is (the customer module
 *   no longer calls the orders API directly, so the public secure cookie is not needed);
 * - picker pages: refresh the session and retry once, else logout + "Sesión Expirada" + `/auth`. In that case any
 *   open Swal is closed, open Material dialogs are closed once on `/auth` (their `afterClosed` searches then fail
 *   as a public page, without a second logout) and the error is rethrown as `SessionExpiredError`, so screens can
 *   skip their error modal.
 *
 * 403 from cc-rt-core (`API_RT_CORE`) outside public pages (no RT account, permission, tenant or store): logout +
 * "Acceso no Autorizado" + `/auth`, rethrown as `SessionExpiredError` like the 401. Other 403 (for example the
 * cc-auth login `PRODUCT_ACCESS_DENIED`) are propagated as is.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const dialog = inject(MatDialog);
  const snack = inject(SnackMessageService);
  const authService = inject(AuthService);
  const tracker = inject(EventTrackerService);
  const env = inject(APP_ENVIRONMENT);

  const track = (result: EventTrackerResult, message: string, extra: Record<string, unknown> = {}) => {
    try {
      tracker.trackStep('interceptor', { result, payload: { message, ...extra } });
    } catch {
      /* ignored */
    }
  };

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // `/session/me/cache` is called by the logout itself: refreshing (and logging out again) would loop.
      if (error.status === 401 && !req.url.includes('/session/refresh') && !req.url.includes('/session/me/cache')) {
        const path = window.location.pathname;

        if (isPublicTokenPath(path)) {
          return throwError(() => error);
        }

        return authService.refreshSession('interceptor', req.url).pipe(
          switchMap(() => next(req.clone())),
          catchError(() => {
            track('fail', 'Session refresh failed, logging out user and redirecting to auth', {
              request_url: req.url,
              path,
            });
            authService.logout().subscribe(() => {
              Swal.close();
              snack.error('Sesión Expirada', 'Su sesión ha expirado, por favor inicie sesión nuevamente');
              router.navigate(['/auth']).then(() => dialog.closeAll());
            });
            return throwError(() => new SessionExpiredError(error));
          }),
        );
      }

      if (error.status === 403 && req.url.startsWith(env.API_RT_CORE)) {
        const path = window.location.pathname;

        if (isPublicTokenPath(path)) {
          return throwError(() => error);
        }

        if (!forbiddenLogoutInProgress) {
          forbiddenLogoutInProgress = true;
          track('fail', 'Forbidden from cc-rt-core, logging out user and redirecting to auth', {
            request_url: req.url,
            path,
          });
          authService
            .logout()
            .pipe(
              finalize(() => {
                forbiddenLogoutInProgress = false;
              }),
            )
            .subscribe(() => {
              Swal.close();
              snack.error(
                'Acceso no Autorizado',
                'Su cuenta ya no tiene acceso a esta funcionalidad, por favor inicie sesión nuevamente',
              );
              router.navigate(['/auth']).then(() => dialog.closeAll());
            });
        }
        return throwError(() => new SessionExpiredError(error));
      }
      return throwError(() => error);
    }),
  );
};
